<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'

import { useGameState } from '@/stores/gameState'

import { getGameLog } from '@/api/getGameLog'
import { useAuth } from '@/stores/auth'
import type { PlayerActionWithMeta } from '@acepoker/contract'

const { getRoomHistoryList } = storeToRefs(useGameState())
const { player: currentPlayer } = storeToRefs(useAuth())

const formatter = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric'
})

const roomHistory = computed(() => getRoomHistoryList.value?.toReversed() ?? [])

// TODO: Prettyfy print the log

function parseLog(action: PlayerActionWithMeta): { msg: string } {
  return { msg: getMessage(action) }
}

function getMessage(action: PlayerActionWithMeta): string {
  const playerName = action.playerId === currentPlayer.value.id ? 'You' : getPlayerName(action)
  switch (action.action) {
    case 'fold':
      return `${playerName} folded`
    case 'check':
      return `${playerName} checked`
    case 'call':
      return `${playerName} called`
    case 'raise':
      return `${playerName} raised ${action.amount}`
    case 'bet':
      return `${playerName} bet ${action.amount}`
    // case 'allIn':
    //   return `${playerName} went all in.`

    // case 'sitDown':
    //   return `${playerName} sat down`
    // case 'sitOut':
    //   return `${playerName} sat out`
    // case 'preflop':
    //   return `New hand started`
    // case 'flop':
    //   return `Flop: ${parseCards(action.communityCards)}`
    // case 'turn':
    //   return `Turn: ${parseCards(action.communityCards)}`
    // case 'river':
    //   return `River: ${parseCards(action.communityCards)}`
    // case 'payout':
    //   return `${playerName} win ${action.amount}. Cards: ${parseCards(action.playerCards)}. Community cards: ${parseCards(action.communityCards)}.`
    default:
      return `${playerName} performed an unknown action`
  }
}

function getPlayerName(action: PlayerActionWithMeta): string {
  // return action.players.find((player) => player.uid === action.playerId)?.name ?? 'Unknown Player'
  return action.name ?? 'Unknown Player'
}

function parseCards(cards: string): string {
  const suitMap: Record<string, string> = {
    h: '♥',
    d: '♦',
    c: '♣',
    s: '♠'
  }
  const cardsArr = cards.match(/.{1,2}/g) ?? []
  return cardsArr
    .map((card) => {
      const rank = card[0].toUpperCase() === 'T' ? '10' : card[0]
      const suit = card[1].toLowerCase()
      return `${rank}${suitMap[suit]}`
    })
    .join('  ')
}
</script>

<template>
  <perfect-scrollbar class="game-log">
    <table class="game-log__table w-full text-left text-sm">
      <transition-group name="log-fade" tag="tbody">
        <tr v-for="row in roomHistory" :key="row.createdAt" class="even:bg-[#262736]">
          <td class="px-4 py-3 align-top w-10">{{ formatter.format(new Date(row.createdAt)) }}</td>
          <td class="px-4 py-3 align-top text-white">{{ parseLog(row).msg }}</td>
        </tr>
      </transition-group>
    </table>
  </perfect-scrollbar>
</template>

<style lang="scss" scoped>
.game-log {
  // width: calc(100% + 32px);
  // margin-left: -16px;
  // margin-right: -16px;
  table-layout: fixed;
  overflow: hidden;
  border-collapse: collapse;
}

.log-fade {
  &-enter-active,
  &-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &-enter-from {
    opacity: 0;
    transform: translateY(-10px);
  }

  &-enter-to {
    opacity: 1;
    transform: translateY(0);
  }

  &-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  &-leave-to {
    opacity: 0;
    transform: translateY(10px);
  }
}
</style>
